import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

import useUpdateSeed from "../../services/useUpdateSeed";
import useSeed from "../../services/useSeed";
import { useEffect, useState } from "react";
import AddressFieldset from "../../components/AddressFieldset";
import { IAddress } from "../../types/seed";
import DeliveryMethodsManagement from "../../components/DeliveryMethodsManagement";
import DeliveryRegionsMap from "../../components/DeliveryRegionsMap";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const toLocalTime = (time: string) => {
  return dayjs.utc(time, "HH:mm:ss").local().format("HH:mm");
};

const fromLocalTime = (time: string) => {
  return dayjs(time, "HH:mm").utc().format("HH:mm:ss");
};

const Profile = () => {
  const { data, isLoading } = useSeed();
  const { mutate } = useUpdateSeed();
  const [name, setName] = useState("");
  const [deliveryWeekday, setDeliveryWeekday] = useState(0);
  const [slug, setSlug] = useState("");
  const [openTime, setOpenTime] = useState("");
  const [openWeekday, setOpenWeekday] = useState(0);
  const [closeTime, setCloseTime] = useState("");
  const [closeWeekday, setCloseWeekday] = useState(0);
  const [seederName, setSeederName] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [pixKey, setPixKey] = useState("");
  const [message, setMessage] = useState("");
  const [addressObj, setAddressObj] = useState<IAddress | null>(null);
  const [isActive, setIsActive] = useState("false");

  useEffect(() => {
    if (data) {
      setName(data.name);
      setDeliveryWeekday(data.delivery_weekday);
      setSlug(data.slug);
      setOpenTime(toLocalTime(data.open_time));
      setOpenWeekday(data.open_weekday);
      setCloseTime(toLocalTime(data.close_time));
      setCloseWeekday(data.close_weekday);
      setSeederName(data.seeder.name);
      setCpf(data.seeder.cpf);
      setPhone(data.seeder.phone);
      setPixKey(data.seeder.pix_key);
      setAddressObj(data.address || null);
      setIsActive(data.is_active ? "true" : "false");
    }
  }, [
    data,
    setName,
    setDeliveryWeekday,
    setSlug,
    setOpenTime,
    setOpenWeekday,
    setCloseTime,
    setCloseWeekday,
    setSeederName,
    setCpf,
    setPhone,
    setPixKey,
    setAddressObj,
    setIsActive,
  ]);

  const handleSave = () => {
    if (data) {
      mutate(
        {
          address: addressObj || null,
          delivery_weekday: deliveryWeekday,
          id: data.id,
          name,
          slug,
          open_time: fromLocalTime(openTime),
          open_weekday: openWeekday,
          close_time: fromLocalTime(closeTime),
          close_weekday: closeWeekday,
          seeder: {
            id: data.seeder.id,
            cpf,
            name: seederName,
            phone,
            pix_key: pixKey,
          },
          is_active: isActive === "true",
        },
        {
          onSuccess: () => {
            setMessage("success");
            window.scrollTo({ top: 0, behavior: "smooth" });
          },
          onError: () => {
            setMessage("error");
            window.scrollTo({ top: 0, behavior: "smooth" });
          },
        },
      );
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        margin: "2rem auto",
        maxWidth: "900px",
        padding: "0 1rem",
      }}
    >
      {message === "success" ? (
        <Alert
          onClose={() => {
            setMessage("");
          }}
          severity="success"
        >
          Perfil salvo com sucesso!
        </Alert>
      ) : message === "error" ? (
        <Alert
          onClose={() => {
            setMessage("");
          }}
          severity="error"
        >
          Erro ao salvar pedido.
        </Alert>
      ) : null}
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
          }}
        >
          <Typography component="h1" variant="h5">
            Perfil da semente
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                sx={{ width: "100%" }}
                label="Nome da semente"
                onChange={(e) => {
                  setName(e.target.value);
                }}
                value={name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                sx={{ width: "100%" }}
                label="Slug"
                onChange={(e) => {
                  setSlug(e.target.value);
                }}
                value={slug}
              />
            </Grid>
          </Grid>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Ativa</InputLabel>
            <Select
              value={isActive}
              onChange={(e) => {
                setIsActive(e.target.value);
              }}
              label="Ativa"
            >
              <MenuItem value={"true"}>Sim</MenuItem>
              <MenuItem value={"false"}>Não</MenuItem>
            </Select>
          </FormControl>
          <AddressFieldset
            address={addressObj}
            onChange={setAddressObj}
            title="Endereço Detalhado"
          />
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Dia da entrega</InputLabel>
            <Select
              value={deliveryWeekday}
              onChange={(e) => {
                setDeliveryWeekday(e.target.value as number);
              }}
              label="Dia da entrega"
            >
              <MenuItem value={0}>Segunda</MenuItem>
              <MenuItem value={1}>Terça</MenuItem>
              <MenuItem value={2}>Quarta</MenuItem>
              <MenuItem value={3}>Quinta</MenuItem>
              <MenuItem value={4}>Sexta</MenuItem>
              <MenuItem value={5}>Sábado</MenuItem>
              <MenuItem value={6}>Domingo</MenuItem>
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                sx={{ width: "100%" }}
                label="Horário de início dos pedidos"
                onChange={(e) => {
                  setOpenTime(e.target.value);
                }}
                value={openTime}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl sx={{ minWidth: 300, width: "100%" }}>
                <InputLabel>Dia de início dos pedidos</InputLabel>
                <Select
                  value={openWeekday}
                  onChange={(e) => {
                    setOpenWeekday(e.target.value as number);
                  }}
                  label="Dia de início dos pedidos"
                >
                  <MenuItem value={0}>Segunda</MenuItem>
                  <MenuItem value={1}>Terça</MenuItem>
                  <MenuItem value={2}>Quarta</MenuItem>
                  <MenuItem value={3}>Quinta</MenuItem>
                  <MenuItem value={4}>Sexta</MenuItem>
                  <MenuItem value={5}>Sábado</MenuItem>
                  <MenuItem value={6}>Domingo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                sx={{ width: "100%" }}
                label="Horário de encerramento dos pedidos"
                onChange={(e) => {
                  setCloseTime(e.target.value);
                }}
                value={closeTime}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl sx={{ minWidth: 300, width: "100%" }}>
                <InputLabel>Dia de encerramento dos pedidos</InputLabel>
                <Select
                  value={closeWeekday}
                  onChange={(e) => {
                    setCloseWeekday(e.target.value as number);
                  }}
                  label="Dia de encerramento dos pedidos"
                >
                  <MenuItem value={0}>Segunda</MenuItem>
                  <MenuItem value={1}>Terça</MenuItem>
                  <MenuItem value={2}>Quarta</MenuItem>
                  <MenuItem value={3}>Quinta</MenuItem>
                  <MenuItem value={4}>Sexta</MenuItem>
                  <MenuItem value={5}>Sábado</MenuItem>
                  <MenuItem value={6}>Domingo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography component="h1" variant="h5">
            Perfil da semeadora
          </Typography>
          <TextField
            label="Nome"
            onChange={(e) => {
              setSeederName(e.target.value);
            }}
            value={seederName}
          />
          <TextField
            label="CPF"
            onChange={(e) => {
              setCpf(e.target.value);
            }}
            value={cpf}
          />
          <TextField
            label="Telefone"
            onChange={(e) => {
              setPhone(e.target.value);
            }}
            value={phone}
          />
          <TextField
            label="Chave PIX"
            onChange={(e) => {
              setPixKey(e.target.value);
            }}
            value={pixKey}
          />
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>

          <DeliveryMethodsManagement />

          <DeliveryRegionsMap />
        </Box>
      )}
    </Box>
  );
};

export default Profile;
